/**
 * Company Service
 * 
 * Handles API calls for company data
 */

import { getApiClient } from './client';
import type { Company, CompanyResponse } from '@/types/company.types';

/**
 * Company API response from backend
 */
interface CompanyApiResponse {
  companyId: number;
  companyName: string;
  companyLogo?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  taxId?: string | null;
}

/**
 * Type guard to validate Company API response structure
 */
function isCompanyApiResponse(obj: unknown): obj is CompanyApiResponse {
  if (typeof obj !== 'object' || obj === null) return false;
  
  const company = obj as Record<string, unknown>;
  
  return (
    typeof company.companyId === 'number' &&
    typeof company.companyName === 'string'
  );
}

/**
 * Fetch company data by company ID
 * 
 * @param companyId - The company ID to fetch
 * @returns Promise with company data
 * @throws Error if request fails or company not found
 */
export async function getCompany(companyId: string | number): Promise<Company> {
  try {
    const apiClient = getApiClient();
    const response = await apiClient.get<CompanyResponse>(`/company/${companyId}`);
    
    if (!response.data || !response.data.item) {
      throw new Error('Invalid response format from API');
    }

    const companyData = response.data.item;

    // Validate response structure
    if (!isCompanyApiResponse(companyData)) {
      throw new Error('Invalid company data structure');
    }

    // Map to Company type (handle null to undefined)
    return {
      ...companyData,
      companyLogo: companyData.companyLogo ?? undefined,
      address: companyData.address ?? undefined,
      city: companyData.city ?? undefined,
      state: companyData.state ?? undefined,
      zipCode: companyData.zipCode ?? undefined,
      phone: companyData.phone ?? undefined,
      email: companyData.email ?? undefined,
      website: companyData.website ?? undefined,
      taxId: companyData.taxId ?? undefined,
    };
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as { response?: { status?: number; data?: { detail?: string } } };
      
      if (axiosError.response?.status === 404) {
        throw new Error(`Invalid Company ID: ${companyId}. Company not found.`);
      }
      
      const errorMessage = axiosError.response?.data?.detail || 'Failed to fetch company data';
      throw new Error(errorMessage);
    }
    
    throw new Error('Failed to fetch company data');
  }
}

/**
 * Fetch multiple companies (if needed in future)
 * 
 * @returns Promise with companies list
 */
export async function getCompanies(): Promise<Company[]> {
  try {
    const apiClient = getApiClient();
    const response = await apiClient.get<{ items: Company[]; count: number }>('/companies');
    
    if (!response.data || !Array.isArray(response.data.items)) {
      throw new Error('Invalid response format from API');
    }

    return response.data.items;
  } catch (error) {
    console.error('Failed to fetch companies:', error);
    throw new Error('Unable to fetch companies');
  }
}
